export interface ZipEntry {
  name: string;
  content: Uint8Array;
}

const LOCAL_HEADER_SIGNATURE = 0x04034b50;
const CENTRAL_HEADER_SIGNATURE = 0x02014b50;
const END_OF_DIRECTORY_SIGNATURE = 0x06054b50;

/** Sem compressão: o arquivo é gravado como está (método "store"). */
const STORE_METHOD = 0;

/** Sinaliza nomes de arquivo em UTF-8. */
const UTF8_FLAG = 0x0800;

const VERSION = 20;

/** Data/hora fixa (01/01/1980) para que o mesmo conteúdo gere sempre o mesmo arquivo. */
const DOS_TIME = 0;
const DOS_DATE = 0x0021;

const CRC_TABLE = buildCrcTable();

function buildCrcTable(): Uint32Array {
  const table = new Uint32Array(256);

  for (let index = 0; index < 256; index += 1) {
    let value = index;

    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }

    table[index] = value >>> 0;
  }

  return table;
}

export function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;

  for (const byte of data) {
    crc = (CRC_TABLE[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
}

function u16(value: number): number[] {
  return [value & 0xff, (value >>> 8) & 0xff];
}

function u32(value: number): number[] {
  return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff];
}

function concat(parts: Uint8Array[]): Uint8Array {
  const size = parts.reduce((total, part) => total + part.length, 0);
  const result = new Uint8Array(size);
  let offset = 0;

  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }

  return result;
}

/**
 * Monta um pacote ZIP sem compressão — o contêiner do formato .xlsx. Cada
 * arquivo entra com o cabeçalho local, e o diretório central no fim lista as
 * posições de todos eles.
 */
export function buildZipFile(entries: ZipEntry[]): Uint8Array {
  const encoder = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const checksum = crc32(entry.content);
    const size = entry.content.length;

    const localHeader = new Uint8Array([
      ...u32(LOCAL_HEADER_SIGNATURE),
      ...u16(VERSION),
      ...u16(UTF8_FLAG),
      ...u16(STORE_METHOD),
      ...u16(DOS_TIME),
      ...u16(DOS_DATE),
      ...u32(checksum),
      ...u32(size),
      ...u32(size),
      ...u16(name.length),
      ...u16(0),
    ]);

    locals.push(localHeader, name, entry.content);

    centrals.push(
      new Uint8Array([
        ...u32(CENTRAL_HEADER_SIGNATURE),
        ...u16(VERSION),
        ...u16(VERSION),
        ...u16(UTF8_FLAG),
        ...u16(STORE_METHOD),
        ...u16(DOS_TIME),
        ...u16(DOS_DATE),
        ...u32(checksum),
        ...u32(size),
        ...u32(size),
        ...u16(name.length),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u16(0),
        ...u32(0),
        ...u32(offset),
      ]),
      name,
    );

    offset += localHeader.length + name.length + size;
  }

  const directory = concat(centrals);

  const endOfDirectory = new Uint8Array([
    ...u32(END_OF_DIRECTORY_SIGNATURE),
    ...u16(0),
    ...u16(0),
    ...u16(entries.length),
    ...u16(entries.length),
    ...u32(directory.length),
    ...u32(offset),
    ...u16(0),
  ]);

  return concat([...locals, directory, endOfDirectory]);
}
